import axios from 'axios';
import { translateProductData, translateCategoryData, addLanguageToAPI } from './dataTranslation';

/**
 * Enhanced API utility that automatically handles language parameters and data translation
 */
class APIWithLanguage {
  constructor() {
    this.currentLanguage = 'ba';
  }

  /**
   * Set current language for API calls
   * @param {string} language - Language code
   */
  setLanguage(language) {
    this.currentLanguage = language || 'ba';
  }

  /**
   * Enhanced GET request with language support
   * @param {string} url - API endpoint
   * @param {Object} config - Axios config
   * @param {Object} translationConfig - Translation configuration
   * @returns {Promise} Axios promise with translated data
   */
  async get(url, config = {}, translationConfig = {}) {
    try {
      const languageUrl = addLanguageToAPI(url, this.currentLanguage);
      const response = await axios.get(languageUrl, config);
      
      if (response.data && translationConfig.enabled) {
        response.data = this.translateResponseData(response.data, translationConfig);
      }
      
      return response;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Enhanced POST request with language support
   * @param {string} url - API endpoint
   * @param {Object} data - Request data
   * @param {Object} config - Axios config
   * @param {Object} translationConfig - Translation configuration
   * @returns {Promise} Axios promise with translated data
   */
  async post(url, data = {}, config = {}, translationConfig = {}) {
    try {
      // Add language to request data
      const requestData = {
        ...data,
        lang: this.currentLanguage
      };

      const response = await axios.post(url, requestData, config);
      
      if (response.data && translationConfig.enabled) {
        response.data = this.translateResponseData(response.data, translationConfig);
      }
      
      return response;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Translate response data based on configuration
   * @param {Object} data - Response data
   * @param {Object} config - Translation configuration
   * @returns {Object} Translated data
   */
  translateResponseData(data, config) {
    if (!data || !config.type) {
      return data;
    }

    // Handle nested response structure
    let targetData = data;
    if (config.dataPath) {
      const pathParts = config.dataPath.split('.');
      for (const part of pathParts) {
        if (targetData && targetData[part]) {
          targetData = targetData[part];
        } else {
          return data; // Return original if path not found
        }
      }
    }

    // Apply translation based on type
    let translatedData;
    switch (config.type) {
      case 'products':
        translatedData = translateProductData(targetData, this.currentLanguage);
        break;
      case 'categories':
        translatedData = translateCategoryData(targetData, this.currentLanguage);
        break;
      default:
        translatedData = targetData;
    }

    // Reconstruct the response with translated data
    if (config.dataPath) {
      const result = { ...data };
      const pathParts = config.dataPath.split('.');
      let current = result;
      
      for (let i = 0; i < pathParts.length - 1; i++) {
        current = current[pathParts[i]];
      }
      
      current[pathParts[pathParts.length - 1]] = translatedData;
      return result;
    }

    return translatedData;
  }

  /**
   * Get products with automatic translation
   * @param {Object} params - Request parameters
   * @returns {Promise} Products data with translation
   */
  async getProducts(params = {}) {
    return this.post(window.ajaxLink + "products", params, {}, {
      enabled: true,
      type: 'products',
      dataPath: 'responseData.products'
    });
  }

  /**
   * Get product sections with automatic translation
   * @returns {Promise} Sections data with translation
   */
  async getProductSections() {
    return this.get(window.ajaxLink + "products/sections", {}, {
      enabled: true,
      type: 'categories',
      dataPath: 'responseData.sectionResults'
    });
  }

  /**
   * Get category data with automatic translation
   * @param {Object} params - Request parameters
   * @returns {Promise} Category data with translation
   */
  async getCategoryData(params = {}) {
    return this.post(window.ajaxLink + "products/category", params, {}, {
      enabled: true,
      type: 'products',
      dataPath: 'responseData'
    });
  }

  /**
   * Search products with automatic translation
   * @param {Object} params - Search parameters
   * @returns {Promise} Search results with translation
   */
  async searchProducts(params = {}) {
    return this.post(window.ajaxLink + "products/filter", params, {}, {
      enabled: true,
      type: 'products',
      dataPath: 'responseData'
    });
  }

  /**
   * Get product details with automatic translation
   * @param {Object} params - Product parameters
   * @returns {Promise} Product details with translation
   */
  async getProductDetails(params = {}) {
    return this.post(window.ajaxLink + "products/item", params, {}, {
      enabled: true,
      type: 'products',
      dataPath: 'responseData'
    });
  }

  /**
   * Get user products with automatic translation
   * @param {Object} params - User parameters
   * @returns {Promise} User products with translation
   */
  async getUserProducts(params = {}) {
    return this.post(window.ajaxLink + "products/user", params, {}, {
      enabled: true,
      type: 'products',
      dataPath: 'responseData'
    });
  }
}

// Create singleton instance
const apiWithLanguage = new APIWithLanguage();

export default apiWithLanguage;
